import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDateTime, formatMoney, KIND_LABEL, KIND_ROUTE, CATEGORY_LABEL } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import TransactionDialog from "@/components/TransactionDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function TransactionsTable({ items = [], showBalance = false, onRowClick, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const link = (kind, id) => {
    if (!id || kind === "external") return null;
    return `/${KIND_ROUTE[kind]}/${id}`;
  };

  const canModify = (t) => t.category !== "invoice_sale" && t.category !== "invoice_purchase";

  const doDelete = async () => {
    try {
      await api.delete(`/transactions/${deleting.id}`);
      toast.success("İşlem silindi");
      setDeleting(null);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="bg-white rounded-md border border-zinc-200 overflow-hidden">
      <table className="acc">
        <thead>
          <tr>
            <th>Tarih</th>
            <th>Kategori</th>
            <th>Gönderen</th>
            <th>Alıcı</th>
            <th className="num">Meblağ</th>
            <th>Açıklama</th>
            {showBalance && <th className="num">Bakiye</th>}
            <th style={{width: 80}}></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={showBalance ? 8 : 7} className="text-center text-zinc-500 py-8">Henüz hareket yok</td></tr>
          )}
          {items.map((t) => {
            const fromLink = link(t.from_kind, t.from_id);
            const toLink = link(t.to_kind, t.to_id);
            const modifiable = canModify(t);
            return (
              <tr key={t.id} data-testid={`tx-row-${t.id}`} onClick={() => onRowClick?.(t)}>
                <td className="whitespace-nowrap text-zinc-600">{formatDateTime(t.date)}</td>
                <td><Badge variant="secondary" className="font-normal">{CATEGORY_LABEL[t.category] || t.category}</Badge></td>
                <td>
                  {fromLink ? (
                    <Link to={fromLink} onClick={(e)=>e.stopPropagation()} className="text-zinc-900 hover:underline">
                      {t.from_name} <span className="text-xs text-zinc-500">({KIND_LABEL[t.from_kind]})</span>
                    </Link>
                  ) : <span className="text-zinc-500">{t.from_name}</span>}
                </td>
                <td>
                  {toLink ? (
                    <Link to={toLink} onClick={(e)=>e.stopPropagation()} className="text-zinc-900 hover:underline">
                      {t.to_name} <span className="text-xs text-zinc-500">({KIND_LABEL[t.to_kind]})</span>
                    </Link>
                  ) : <span className="text-zinc-500">{t.to_name}</span>}
                </td>
                <td className="num">{formatMoney(t.amount, t.currency)}</td>
                <td className="text-zinc-600 max-w-[280px] truncate">{t.description}</td>
                {showBalance && (
                  <td className={`num ${(t.balance_after || 0) >= 0 ? "pos" : "neg"}`}>{formatMoney(t.balance_after, t.currency)}</td>
                )}
                <td onClick={(e)=>e.stopPropagation()}>
                  {modifiable ? (
                    <div className="flex gap-1">
                      <button
                        data-testid={`tx-edit-${t.id}`}
                        onClick={()=>setEditing(t)}
                        className="text-zinc-400 hover:text-zinc-900 p-1"
                        aria-label="Düzenle"
                      ><Pencil size={14} /></button>
                      <button
                        data-testid={`tx-delete-${t.id}`}
                        onClick={()=>setDeleting(t)}
                        className="text-zinc-400 hover:text-red-600 p-1"
                        aria-label="Sil"
                      ><Trash2 size={14} /></button>
                    </div>
                  ) : (
                    <span className="text-xs text-zinc-400" title="Fatura hareketi">Otomatik</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <TransactionDialog
        open={!!editing}
        onClose={()=>setEditing(null)}
        editing={editing}
        onSaved={()=>{ setEditing(null); onChanged?.(); }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o)=>!o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>İşlem silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu hareket silindiğinde ilgili cari bakiyeler otomatik olarak geri alınacaktır. Devam etmek istiyor musunuz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="tx-confirm-delete" onClick={doDelete} className="bg-red-700 hover:bg-red-800">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
